import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Spinner from 'react-bootstrap/Spinner';
import apiClient, { API_ENDPOINTS } from '../api/config';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../utils/apiError';

type Feature = {
  name: string;
  area: string;
  description: string;
  available_actions: string[];
  evidence_url: string;
  evidence_page_title?: string | null;
  confidence: 'observed' | 'inferred' | string;
  access?: 'public' | 'authenticated';
};

type Workflow = { name: string; steps: string[]; confidence: string };

type PlannedTestStep = {
  step_id?: string | null;
  name: string;
  purpose: string;
  expected_outcome: string;
  requires_form_input: boolean;
  requires_docx: boolean;
  access: 'public' | 'authenticated';
  verification?: 'semantic' | 'visual' | 'both';
  goal_state?: string;
  allowed_effects?: Array<'navigate' | 'form_input' | 'submit' | 'upload' | 'create_record'>;
  source_instruction?: string;
  source_step_number?: string | null;
};

type TestPlan = {
  summary: string;
  steps: PlannedTestStep[];
  notes: string[];
  requires_authentication: boolean;
  approval_required: boolean;
};

type CompletedCheck = {
  step_id?: string | null;
  name: string;
  status: 'passed' | 'failed' | 'skipped' | 'observed';
  observed_outcome: string;
  evidence_url?: string | null;
  evidence_artifacts?: string[];
  visual_verdict?: 'verified' | 'not_verified' | 'inconclusive' | null;
  visual_reason?: string | null;
  recovery_actions?: Array<{
    action: string;
    reason: string;
    result: string;
    url?: string | null;
  }>;
};

type PhaseLimitations = {
  phase: 'public_mapping' | 'authenticated_mapping' | 'execution';
  items: string[];
};

type ExplorationResult = {
  application_summary: string;
  discovered_pages: string[];
  features: Feature[];
  inferred_workflows: Workflow[];
  limitations: string[];
  phase_limitations: PhaseLimitations[];
  generated_data: { field: string; value: string }[];
  artifacts: { filename: string; kind: string; description: string; step_id?: string | null }[];
  created_records: string[];
  test_plan: TestPlan;
  completed_checks: CompletedCheck[];
  execution_outcome?: string | null;
};

type ToolEvent = { timestamp: string; tool: string; action: string; url?: string | null };

type ExplorationRun = {
  run_id: string;
  url: string;
  instruction: string;
  status: 'queued' | 'planning' | 'awaiting_approval' | 'executing' | 'completed' | 'failed' | 'running' | 'cancelling' | 'cancelled';
  phase: 'public_mapping' | 'authentication' | 'authenticated_mapping' | 'planning' | 'awaiting_approval' | 'executing' | 'verifying' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  started_at?: string | null;
  elapsed_seconds: number;
  completed_steps: number;
  total_steps: number;
  current_tool?: string | null;
  current_action?: string | null;
  current_url?: string | null;
  authentication_session_expires_at?: string | null;
  events: ToolEvent[];
  result?: ExplorationResult | null;
  error?: string | null;
};

const ACTIVE_STATUSES = new Set(['queued', 'planning', 'executing', 'running', 'cancelling']);

function formatElapsed(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${remainingSeconds}s`;
}

function labelize(value?: string | null): string {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase()) || '—';
}

export default function ExploratoryAgent(): React.ReactElement {
  const { user } = useAuth();
  const [url, setUrl] = useState('');
  const [instruction, setInstruction] = useState('');
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [run, setRun] = useState<ExplorationRun | null>(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [evidencePreviews, setEvidencePreviews] = useState<Record<string, string>>({});

  const active = Boolean(run && ACTIVE_STATUSES.has(run.status));
  const requiresAuthentication = Boolean(run?.result?.test_plan.requires_authentication);
  const authenticationSessionExpiry = run?.authentication_session_expires_at
    ? new Date(run.authentication_session_expires_at).getTime()
    : null;
  const authenticationSessionExpired = Boolean(
    requiresAuthentication
    && (!authenticationSessionExpiry || Number.isNaN(authenticationSessionExpiry) || authenticationSessionExpiry <= now),
  );
  const hasActiveOrganization = Boolean(user?.active_organization_id);
  const groupedFeatures = useMemo(() => {
    const groups = new Map<string, Feature[]>();
    for (const feature of run?.result?.features || []) {
      const area = feature.area || 'General';
      groups.set(area, [...(groups.get(area) || []), feature]);
    }
    return Array.from(groups.entries());
  }, [run?.result?.features]);

  const fetchRun = useCallback(async (runId: string) => {
    const response = await apiClient.get<ExplorationRun>(API_ENDPOINTS.EXPLORATORY_AGENT_RUN(runId));
    setRun(response.data);
    return response.data;
  }, []);

  useEffect(() => {
    if (!run?.run_id || !active) return undefined;
    const interval = window.setInterval(() => {
      fetchRun(run.run_id).catch((requestError: any) => {
        setError(formatApiError(requestError, 'Could not refresh exploration status.'));
      });
    }, 3000);
    return () => window.clearInterval(interval);
  }, [active, fetchRun, run?.run_id]);

  useEffect(() => {
    if (!active && run?.status !== 'awaiting_approval') return undefined;
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [active, run?.status]);

  useEffect(() => {
    const filenames = Array.from(new Set(
      (run?.result?.completed_checks || [])
        .flatMap((check) => check.evidence_artifacts || [])
        .filter((filename) => /\.(png|jpe?g)$/i.test(filename)),
    ));
    if (!run?.run_id || filenames.length === 0) {
      setEvidencePreviews({});
      return undefined;
    }
    let cancelled = false;
    const objectUrls: string[] = [];
    void Promise.all(filenames.map(async (filename) => {
      const response = await apiClient.get(
        API_ENDPOINTS.EXPLORATORY_AGENT_ARTIFACT(run.run_id, filename),
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      objectUrls.push(url);
      return [filename, url] as const;
    })).then((entries) => {
      if (!cancelled) setEvidencePreviews(Object.fromEntries(entries));
    }).catch(() => {
      if (!cancelled) setEvidencePreviews({});
    });
    return () => {
      cancelled = true;
      objectUrls.forEach((url) => window.URL.revokeObjectURL(url));
    };
  }, [run?.run_id, run?.result?.completed_checks]);

  useEffect(() => {
    const previousTitle = document.title;
    if (run?.status === 'completed') document.title = 'Exploration completed · AcceliQA';
    else if (run?.status === 'failed') document.title = 'Exploration failed · AcceliQA';
    else if (run?.status === 'cancelled') document.title = 'Exploration cancelled · AcceliQA';
    else if (active) document.title = `${labelize(run?.phase)} · Exploring`;
    return () => { document.title = previousTitle; };
  }, [active, run?.phase, run?.status]);

  const elapsedSeconds = useMemo(() => {
    if (!run) return 0;
    if (!run.started_at || !active) return run.elapsed_seconds || 0;
    const startedAt = new Date(run.started_at).getTime();
    return Math.max(run.elapsed_seconds || 0, Math.floor((now - startedAt) / 1000));
  }, [active, now, run]);

  async function startExploration(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError('');
    const value = url.trim();
    if (!/^https?:\/\//i.test(value)) {
      setError('Enter a public URL beginning with http:// or https://.');
      return;
    }
    setSubmitting(true);
    try {
      const authRequested = Boolean(loginIdentifier.trim() || password);
      if (authRequested && (!loginIdentifier.trim() || !password)) {
        setError('Enter both username or email and password for active exploration.');
        return;
      }
      const response = await apiClient.post<ExplorationRun>(API_ENDPOINTS.EXPLORATORY_AGENT_RUNS, {
        url: value,
        instruction: instruction.trim(),
        ...(authRequested ? { login_identifier: loginIdentifier.trim(), password } : {}),
      });
      setPassword('');
      setRun(response.data);
    } catch (requestError: any) {
      setError(formatApiError(requestError, 'Could not start exploration.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function approvePlan(): Promise<void> {
    if (!run?.result?.test_plan) return;
    setError('');
    if (authenticationSessionExpired) {
      setError('Authenticated session expired; start a new exploration.');
      return;
    }
    setApproving(true);
    try {
      const response = await apiClient.post<ExplorationRun>(
        API_ENDPOINTS.EXPLORATORY_AGENT_APPROVE(run.run_id),
        {},
      );
      setRun(response.data);
    } catch (requestError: any) {
      setError(formatApiError(requestError, 'Could not start the submitted instructions.'));
    } finally {
      setApproving(false);
    }
  }

  async function cancelExploration(): Promise<void> {
    if (!run || cancelling || !active) return;
    if (!window.confirm('Cancel this exploration? The agent will stop at its next safe browser checkpoint. Any form already submitted on the authorized test site cannot be undone.')) return;
    setError('');
    setCancelling(true);
    try {
      const response = await apiClient.post<ExplorationRun>(API_ENDPOINTS.EXPLORATORY_AGENT_CANCEL(run.run_id));
      setRun(response.data);
    } catch (requestError: any) {
      setError(formatApiError(requestError, 'Could not cancel exploration.'));
    } finally {
      setCancelling(false);
    }
  }

  async function downloadArtifact(filename: string): Promise<void> {
    if (!run) return;
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.EXPLORATORY_AGENT_ARTIFACT(run.run_id, filename),
        { responseType: 'blob' },
      );
      const objectUrl = window.URL.createObjectURL(new Blob([response.data]));
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = filename;
      anchor.click();
      window.URL.revokeObjectURL(objectUrl);
    } catch (requestError: any) {
      setError(formatApiError(requestError, `Could not download ${filename}.`));
    }
  }

  function openEvidence(filename: string): void {
    const previewUrl = evidencePreviews[filename];
    if (previewUrl) window.open(previewUrl, '_blank', 'noopener,noreferrer');
    else void downloadArtifact(filename);
  }

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: '24px 0 48px' }}>
      <div className="mb-4">
        <h1 className="h3 mb-2">Exploratory Agent</h1>
        <p className="text-muted mb-0">
          Give the agent an authorized website and testing goal. It maps the app, proposes a plan, then runs only after your approval.
        </p>
      </div>

      {!hasActiveOrganization ? (
        <Alert variant="info" className="mb-4">
          Select an organization from the sidebar to start an exploratory run. Exploratory Agent access is managed separately for each organization.
        </Alert>
      ) : (
      <Card className="shadow-sm mb-4">
        <Card.Body>
          <Form onSubmit={startExploration}>
            <Form.Group controlId="exploratory-agent-url" className="mb-3">
              <Form.Label>Application URL</Form.Label>
              <div className="d-flex gap-2 flex-column flex-md-row">
                <Form.Control
                  type="url"
                  value={url}
                  onChange={(event) => setUrl(event.target.value)}
                  placeholder="https://example.com"
                  disabled={active || submitting}
                  required
                />
                <Button type="submit" disabled={active || submitting} style={{ minWidth: 132 }}>
                  {(submitting || active) && <Spinner animation="border" size="sm" className="me-2" />}
                  {active ? 'Exploring…' : 'Explore'}
                </Button>
              </div>
            </Form.Group>
            <Form.Group controlId="exploratory-agent-instruction" className="mb-3">
              <Form.Label>What should the agent explore or test?</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={instruction}
                onChange={(event) => setInstruction(event.target.value)}
                placeholder="For example: Explore loan creation and verify whether a user can submit an application with a document."
                disabled={active || submitting}
                required
              />
            </Form.Group>
            <div className="row g-2">
              <Form.Group className="col-md-6" controlId="exploratory-agent-login-identifier">
                <Form.Label>Test account username or email <span className="text-muted small">(optional)</span></Form.Label>
                <Form.Control type="text" value={loginIdentifier} onChange={(event) => setLoginIdentifier(event.target.value)} disabled={active || submitting} placeholder="tester@example.com or test.user" autoComplete="username" />
              </Form.Group>
              <Form.Group className="col-md-6" controlId="exploratory-agent-password">
                <Form.Label>Test account password <span className="text-muted small">(optional)</span></Form.Label>
                <Form.Control type="password" value={password} onChange={(event) => setPassword(event.target.value)} disabled={active || submitting} autoComplete="new-password" />
              </Form.Group>
            </div>
            <Form.Text className="text-muted">
              Credentials are submitted once for platform-managed authentication and then discarded. A private encrypted browser session is retained for up to 30 minutes so an approved plan can run without signing in again. Do not use a production account.
            </Form.Text>
          </Form>
        </Card.Body>
      </Card>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      {run && (
        <Card className="shadow-sm mb-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
              <div>
                <h2 className="h5 mb-1">Live exploration</h2>
                <a href={run.url} target="_blank" rel="noreferrer" className="small text-break">{run.url}</a>
              </div>
              <div className="d-flex align-items-center gap-2">
                {active && <Button variant="outline-danger" size="sm" onClick={cancelExploration} disabled={cancelling || run.status === 'cancelling'}>{cancelling || run.status === 'cancelling' ? 'Cancelling…' : 'Cancel run'}</Button>}
                <Badge bg={run.status === 'completed' ? 'success' : run.status === 'failed' ? 'danger' : run.status === 'cancelled' ? 'secondary' : run.status === 'awaiting_approval' ? 'warning' : 'primary'}>
                  {labelize(run.status)}
                </Badge>
              </div>
            </div>
            <div className="d-flex justify-content-between small text-muted mb-1">
              <span><strong>Phase:</strong> {labelize(run.phase)}</span>
              <span><strong>Elapsed:</strong> {formatElapsed(elapsedSeconds)}</span>
            </div>
            <ProgressBar now={run.progress} className="mb-2" />
            <div className="small text-muted mb-3">
              {run.total_steps > 0 ? `${run.completed_steps} of ${run.total_steps} approved checks confirmed` : 'Mapping the application and preparing the next safe step.'}
            </div>
            <div className="small">
              <div><strong>Current tool:</strong> {labelize(run.current_tool)}</div>
              <div><strong>Current action:</strong> {run.current_action || 'Waiting to start'}</div>
              <div><strong>Current URL:</strong> {run.current_url ? <a href={run.current_url} target="_blank" rel="noreferrer" className="text-break">{run.current_url}</a> : 'Waiting to navigate'}</div>
            </div>
            {active && elapsedSeconds >= 30 && <Alert variant="info" className="small mt-3 mb-0">Still working — {run.current_action || 'checking the current browser state'}.</Alert>}
            {run.error && <Alert variant="danger" className="mt-3 mb-0">{run.error}</Alert>}
            {run.result?.execution_outcome === 'partial_timeout' && (
              <Alert variant="warning" className="mt-3 mb-0">
                Timed out with partial progress. Completed steps and their evidence are retained below; this run will not be replayed automatically.
              </Alert>
            )}

            <hr />
            <h3 className="h6">Activity timeline</h3>
            <ol className="small mb-0 ps-3" style={{ maxHeight: 340, overflowY: 'auto' }}>
              {run.events.map((item, index) => (
                <li key={`${item.timestamp}-${index}`} className="mb-1">
                  <strong>{labelize(item.tool)}:</strong> {item.action}
                  {item.url && <> <a href={item.url} target="_blank" rel="noreferrer" className="text-muted">({item.url})</a></>}
                </li>
              ))}
            </ol>
          </Card.Body>
        </Card>
      )}

      {run?.result && (run.status === 'awaiting_approval' || run.status === 'completed' || run.status === 'failed') && (
        <Card className="shadow-sm">
          <Card.Body>
            <h2 className="h4">{run.status === 'awaiting_approval' ? 'Your instruction steps' : 'Exploration results'}</h2>
            <p>{run.result.application_summary}</p>

            <section className="mb-4">
              <div className="d-flex align-items-center gap-2 mb-2">
                <h3 className="h5 mb-0">Your execution instructions</h3>
                <Badge bg="secondary">Instruction contract</Badge>
              </div>
              <p className="mb-2">{run.result.test_plan.summary || 'The agent will follow your instructions in the supplied order.'}</p>
              {run.result.test_plan.steps.length > 0 && (
                <ol className="mb-2 ps-0" style={{ listStyle: 'none' }}>
                  {run.result.test_plan.steps.map((step, index) => {
                    const check = run.result?.completed_checks.find((item) => item.step_id === step.step_id)
                      || (!run.result?.completed_checks.some((item) => item.step_id) ? run.result?.completed_checks[index] : undefined);
                    const screenshot = (check?.evidence_artifacts || []).find((filename) => /\.(png|jpe?g)$/i.test(filename));
                    return (
                    <li key={step.step_id || `${step.name}-${step.expected_outcome}`} className="mb-3">
                      <strong>{step.source_step_number ? `${step.source_step_number}. ` : ''}{step.name}</strong>
                      <div className="small text-body mt-1" style={{ whiteSpace: 'pre-wrap' }}>
                        {step.source_instruction || step.purpose}
                      </div>
                      <div className="small text-muted">
                        Expected: browser-observed completion of this instruction · {step.access} access
                        {step.requires_form_input ? ' · fills synthetic form data' : ''}
                        {step.requires_docx ? ' · generates and uploads a DOCX' : ''}
                      </div>
                      {check && <div className="border rounded p-2 mt-2 bg-white">
                        <Badge bg={check.status === 'passed' ? 'success' : check.status === 'failed' ? 'danger' : check.status === 'skipped' ? 'warning' : 'secondary'} className="me-2">{check.status}</Badge>
                        <span className="small">{check.observed_outcome}</span>
                        {check.visual_verdict && <div className="small mt-1"><strong>Visual verification:</strong> {check.visual_verdict}{check.visual_reason ? ` — ${check.visual_reason}` : ''}</div>}
                        {(check.recovery_actions || []).length > 0 && (
                          <div className="small mt-2 border-start border-info border-3 ps-2">
                            <strong>Recovered automatically</strong>
                            <ul className="mb-0 mt-1 ps-3">
                              {(check.recovery_actions || []).map((action, recoveryIndex) => (
                                <li key={`${action.action}-${recoveryIndex}`}>
                                  {action.action} — {action.reason} {action.result}
                                  {action.url && <> <a href={action.url} target="_blank" rel="noreferrer">({action.url})</a></>}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {screenshot && <div className="mt-2">
                          {evidencePreviews[screenshot] && <img src={evidencePreviews[screenshot]} alt={`Evidence for ${step.name}`} style={{ maxWidth: 320, maxHeight: 180, objectFit: 'contain' }} className="border rounded d-block mb-1" />}
                          <Button variant="outline-primary" size="sm" onClick={() => openEvidence(screenshot)}>View evidence</Button>
                        </div>}
                      </div>}
                    </li>
                    );
                  })}
                </ol>
              )}
              {run.result.test_plan.notes.length > 0 && <Alert variant="secondary" className="mb-0"><ul className="mb-0">{run.result.test_plan.notes.map((note) => <li key={note}>{note}</li>)}</ul></Alert>}
            </section>

            {run.status === 'awaiting_approval' && (
              <section className="border rounded p-3 mb-4 bg-light">
                <h3 className="h5">Run these instructions</h3>
                <p className="small text-muted mb-2">No forms, uploads, or records have been changed yet. The agent will follow the instructions above in order and show any safe recovery action it needs. {requiresAuthentication ? 'The verified encrypted browser session will be reused; the application will not be signed into again.' : 'This run will execute in the public browser session.'}</p>
                {requiresAuthentication && authenticationSessionExpiry && !authenticationSessionExpired && (
                  <Alert variant="info" className="small py-2">
                    Authenticated session available until {new Date(authenticationSessionExpiry).toLocaleTimeString()}.
                  </Alert>
                )}
                {authenticationSessionExpired && (
                  <Alert variant="warning" className="small py-2">
                    Authenticated session expired. Start a new exploration to authenticate again safely.
                  </Alert>
                )}
                <Button onClick={approvePlan} disabled={approving || authenticationSessionExpired}>
                  {approving && <Spinner animation="border" size="sm" className="me-2" />}
                  Run instructions
                </Button>
              </section>
            )}

            {groupedFeatures.map(([area, features]) => (
              <section key={area} className="mb-4">
                <h3 className="h5 border-bottom pb-2">{area}</h3>
                {features.map((feature) => (
                  <div key={`${area}-${feature.name}`} className="mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <strong>{feature.name}</strong>
                      <Badge bg={feature.confidence === 'observed' ? 'success' : 'secondary'}>{feature.confidence}</Badge>
                      <Badge bg={feature.access === 'authenticated' ? 'primary' : 'info'}>{feature.access || 'public'}</Badge>
                    </div>
                    <div className="text-muted small">{feature.description}</div>
                    {feature.available_actions.length > 0 && (
                      <div className="small mt-1"><strong>Visible actions:</strong> {feature.available_actions.join(', ')}</div>
                    )}
                    <a href={feature.evidence_url} target="_blank" rel="noreferrer" className="small">Evidence page</a>
                  </div>
                ))}
              </section>
            ))}

            {run.result.inferred_workflows.length > 0 && (
              <section className="mb-4">
                <h3 className="h5">Inferred workflows</h3>
                {run.result.inferred_workflows.map((workflow) => (
                  <div key={workflow.name} className="mb-2">
                    <strong>{workflow.name}</strong>: {workflow.steps.join(' → ')}
                  </div>
                ))}
              </section>
            )}

            {(run.result.phase_limitations?.some((group) => group.items.length > 0) || run.result.limitations.length > 0) && (
              <Alert variant="secondary" className="mb-0">
                <strong>Exploration limitations:</strong>
                {run.result.phase_limitations?.length > 0 ? (
                  <div className="mt-2">
                    {run.result.phase_limitations.filter((group) => group.items.length > 0).map((group) => (
                      <div key={group.phase} className="mb-2">
                        <strong className="small">{group.phase === 'public_mapping' ? 'Public mapping before login' : group.phase === 'authenticated_mapping' ? 'Authenticated mapping' : 'Approved execution'}</strong>
                        <ul className="mb-0 mt-1">
                          {group.items.map((limitation) => <li key={`${group.phase}-${limitation}`}>{limitation}</li>)}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  <ul className="mb-0 mt-2">
                    {run.result.limitations.map((limitation) => <li key={limitation}>{limitation}</li>)}
                  </ul>
                )}
              </Alert>
            )}

            {run.result.generated_data.length > 0 && (
              <section className="mt-4">
                <h3 className="h5">Generated test data</h3>
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead><tr><th>Field</th><th>Generated value</th></tr></thead>
                    <tbody>{run.result.generated_data.map((item) => <tr key={`${item.field}-${item.value}`}><td>{item.field}</td><td className="text-break">{item.value}</td></tr>)}</tbody>
                  </table>
                </div>
              </section>
            )}

            {run.result.artifacts.length > 0 && (
              <section className="mt-4">
                <h3 className="h5">Generated files and evidence</h3>
                {run.result.artifacts.map((artifact) => (
                  <div key={artifact.filename} className="d-flex align-items-center justify-content-between gap-3 border rounded p-2 mb-2">
                    <div><strong>{artifact.filename}</strong><div className="small text-muted">{artifact.description}</div></div>
                    <Button variant="outline-primary" size="sm" onClick={() => downloadArtifact(artifact.filename)}>Download</Button>
                  </div>
                ))}
              </section>
            )}

            {run.result.created_records.length > 0 && (
              <section className="mt-4">
                <h3 className="h5">Created records</h3>
                <ul className="mb-0">{run.result.created_records.map((record) => <li key={record}>{record}</li>)}</ul>
              </section>
            )}

            {run.status === 'completed' && run.result.completed_checks.length > 0 && (
              <section className="mt-4">
                <h3 className="h5">Completed checks</h3>
                {run.result.completed_checks.map((check) => (
                  <div key={`${check.name}-${check.observed_outcome}`} className="border rounded p-2 mb-2">
                    <Badge bg={check.status === 'passed' ? 'success' : check.status === 'failed' ? 'danger' : check.status === 'skipped' ? 'warning' : 'secondary'} className="me-2">{check.status}</Badge>
                    <strong>{check.name}</strong><div className="small text-muted mt-1">{check.observed_outcome}</div>
                    {check.evidence_url && <a href={check.evidence_url} target="_blank" rel="noreferrer" className="small">Evidence page</a>}
                  </div>
                ))}
              </section>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  );
}
